import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import bookingService from "../../../../../../services/booking.service";
import { useEyeconxStore } from "../../../../../../store/eyeconxStore";
import Loading from "../../../../../common/Loading";

const DoctorHours = ({ doctor }) => {
    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const [doctorHours, setDoctorHours] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    const { register, handleSubmit, reset, formState: { errors }, setValue, watch } = useForm();
    const watchIsOff = watch('isOff', false);

    useEffect(() => {
        if (doctor?.id) {
            fetchDoctorHours();
        }
    }, [doctor]);

    const fetchDoctorHours = async () => {
        setLoading(true);
        try {
            const res = await bookingService.getDoctorAvailability(clinicId, doctor.id);
            setDoctorHours(res.data);
        } catch (error) {
            console.log('Error fetching doctor hours:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateDoctorHours = async (dayId, data) => {
        try {
            const dayData = {
                id: dayId,
                clinicId: clinicId,
                userId: doctor.id,
                day: data.day,
                isOff: data.isOff,
                startTime: data.isOff ? null : data.startTime,
                endTime: data.isOff ? null : data.endTime,
                lunchFrom : data.isOff? null : data.lunchFrom,
                lunchTo : data.isOff? null : data.lunchTo,
            };

            const res = await bookingService.updateDoctorAvailability(clinicId, doctor.id, dayData);

            setDoctorHours(prev =>
                prev.map(day =>
                    day.id === dayId
                        ? { ...day, ...res.data, isOff: data.isOff, startTime: data.startTime, endTime: data.endTime, lunchFrom: data.lunchFrom, lunchTo: data.lunchTo }
                        : day
                )
            );
            setEditingId(null);
        } catch (error) {
            console.log('Error updating doctor hours:', error);
        }
    };

    const deleteDoctorHours = async (dayId) => {
        try {
            // For doctor hours, we might want to reset to default instead of delete
            // Or implement a reset functionality
            const defaultData = {
                id: dayId,
                clinicId: clinicId,
                userId: doctor.id,
                day: doctorHours.find(day => day.id === dayId)?.day,
                isOff: true,
                startTime: null,
                endTime: null,
                lunchFrom : null,
                lunchTo : null,
            };

            await bookingService.updateDoctorAvailability(clinicId, doctor.id, defaultData);
            await fetchDoctorHours();
            setDeleteConfirm(null);
        } catch (error) {
            console.log('Error resetting doctor hours:', error);
        }
    };

    const handleEdit = (day) => {
        setEditingId(day.id);
        setValue('day', day.day);
        setValue('isOff', day.isOff);
        setValue('startTime', day.startTime?.substring(0, 5) || '09:00');
        setValue('endTime', day.endTime?.substring(0, 5) || '17:00');
        setValue('lunchFrom', day.lunchFrom?.substring(0, 5) || '12:00');
        setValue('lunchTo', day.lunchTo?.substring(0, 5) || '13:00');
    };

    const handleCancel = () => {
        setEditingId(null);
        reset();
    };

    const formatTimeDisplay = (timeString) => {
        if (!timeString) return '-';
        const time = timeString.substring(0, 5);
        return time;
    };

    const formatDayName = (day) => {
        const dayMap = {
            'MONDAY': 'Monday',
            'TUESDAY': 'Tuesday',
            'WEDNESDAY': 'Wednesday',
            'THURSDAY': 'Thursday',
            'FRIDAY': 'Friday',
            'SATURDAY': 'Saturday',
            'SUNDAY': 'Sunday'
        };
        return dayMap[day] || day;
    };



    const generateTimeOptions = () => {
        const options = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                options.push(
                    <option key={timeString} value={timeString}>
                        {timeString}
                    </option>
                );
            }
        }
        return options;
    };

    if (loading) {
        return <Loading />;
    }

    return (
        <>
            <div className="card">
                <div className="card-body">


            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h4 className="mb-0">
                        <i className="bi bi-clock me-2 text-primary"></i> Doctor Availability
                    </h4>
                    <p className="text-muted mb-0">Manage weekly working hours for Dr. {doctor?.firstName} {doctor?.lastName}</p>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted mt-2">Loading availability...</p>
                </div>
            ) : doctorHours.length === 0 ? (
                <div className="text-center py-5">
                    <i className="bi bi-calendar-x display-1 text-muted"></i>
                    <h5 className="text-muted mt-3">No availability settings found</h5>
                    <p className="text-muted">Availability settings will be created automatically</p>
                    <button className="btn btn-primary mt-2" onClick={fetchDoctorHours}>
                        <i className="bi bi-arrow-clockwise me-2"></i> Refresh
                    </button>
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-hover align-middle table-striped">
                        <thead>
                        <tr>
                            <th scope="col">Status</th>
                            <th scope="col" className="ps-4"><i className="bi bi-calendar me-2"></i> Day</th>
                            <th scope="col"><i className="bi bi-clock me-2"></i> Time</th>
                            <th scope="col"><i className="bi bi-egg-fried me-2"></i> Lunch Time</th>
                            <th scope="col" className="text-end pe-4">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {doctorHours.map((day) => (
                            <tr key={day.id} className="border-top position-relative">
                                {/* Delete Confirmation Overlay */}
                                {deleteConfirm?.id === day.id && (
                                    <td colSpan="7" className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-90 d-flex justify-content-between align-items-center px-4" style={{ zIndex: 10 }}>
                                        <div className="d-flex align-items-center">
                                            <i className="bi bi-exclamation-triangle-fill text-warning me-2 fs-4"></i>
                                            <strong>Reset {formatDayName(day.day)} to day off?</strong>
                                        </div>
                                        <div className="btn-group">
                                            <button className="btn btn-warning text-white" onClick={() => deleteDoctorHours(day.id)}>
                                                <i className="bi bi-arrow-clockwise me-1"></i> Reset
                                            </button>
                                            <button className="btn btn-outline-secondary" onClick={() => setDeleteConfirm(null)}>
                                                Cancel
                                            </button>
                                        </div>
                                    </td>
                                )}

                                <td>
                                    {editingId === day.id ? (
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                {...register('isOff')}
                                            />
                                        </div>
                                    ) : (
                                        <span className={`badge ${day.isOff ? 'bg-secondary' : 'bg-success'} text-white fs-6`}>
                                                <i className={`bi ${day.isOff ? 'bi-pause-circle' : 'bi-check-circle'} me-1`}></i>
                                            {day.isOff ? 'Day Off' : 'Available'}
                                            </span>
                                    )}
                                </td>
                                <td className="ps-4">
                                    <div className="d-flex align-items-center">

                                        <div className="flex-grow-1">
                                            {editingId === day.id ? (
                                                <select
                                                    className="form-select"
                                                    {...register('day', { required: 'Day is required' })}
                                                    disabled // Day should not be editable
                                                >
                                                    <option value="MONDAY">Monday</option>
                                                    <option value="TUESDAY">Tuesday</option>
                                                    <option value="WEDNESDAY">Wednesday</option>
                                                    <option value="THURSDAY">Thursday</option>
                                                    <option value="FRIDAY">Friday</option>
                                                    <option value="SATURDAY">Saturday</option>
                                                    <option value="SUNDAY">Sunday</option>
                                                </select>
                                            ) : (
                                                <>
                                                    <h6 className="mb-0 fw-semibold">{formatDayName(day.day)}</h6>

                                                </>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <div className="d-flex align-items-center just">
                                    {editingId === day.id ? (
                                        <select
                                            className={`form-select ${errors.startTime ? 'is-invalid' : ''}`}
                                            {...register('startTime', {
                                                required: watchIsOff ? false : 'Start time is required when available'
                                            })}
                                            disabled={watchIsOff}
                                        >
                                            {generateTimeOptions()}
                                        </select>
                                    ) : (
                                        <span className={day.isOff ? 'text-muted' : 'fw-semibold'}>
                                                {day.isOff ? '-' : formatTimeDisplay(day.startTime)}
                                            </span>
                                    )}
                                        {day.isOff ?<div> - </div>: <div className="text-muted ms-2 me-2">to</div>}
                                    {editingId === day.id ? (
                                        <select
                                            className={`form-select ${errors.endTime ? 'is-invalid' : ''}`}
                                            {...register('endTime', {
                                                required: watchIsOff ? false : 'End time is required when available'
                                            })}
                                            disabled={watchIsOff}
                                        >
                                            {generateTimeOptions()}
                                        </select>
                                    ) : (
                                        <span className={day.isOff ? 'text-muted' : 'fw-semibold'}>
                                                {day.isOff ? '-' : formatTimeDisplay(day.endTime)}
                                            </span>
                                    )}
                                    </div>
                                </td>
                                <td>
                                    <div className="d-flex align-items-center">
                                        {editingId === day.id ? (
                                            <select
                                                className={`form-select ${errors.lunchFrom ? 'is-invalid' : ''}`}
                                                {...register('lunchFrom', {
                                                    required: watchIsOff ? false : 'Start time is required when available'
                                                })}
                                                disabled={watchIsOff}
                                            >
                                                {generateTimeOptions()}
                                            </select>
                                        ) : (
                                            <span className={day.isOff ? 'text-muted' : 'fw-semibold'}>
                                                {day.isOff ? '-' : formatTimeDisplay(day.lunchFrom)}
                                            </span>
                                        )}
                                        {day.lunchFrom && <div className="text-muted ms-2 me-2">to</div>}
                                        {editingId === day.id ? (
                                            <select
                                                className={`form-select ${errors.lunchTo ? 'is-invalid' : ''}`}
                                                {...register('lunchTo', {
                                                    required: watchIsOff ? false : 'End time is required when available'
                                                })}
                                                disabled={watchIsOff}
                                            >
                                                {generateTimeOptions()}
                                            </select>
                                        ) : (
                                            <span className={day.isOff ? 'text-muted' : 'fw-semibold'}>
                                                {day.isOff ? '-' : formatTimeDisplay(day.lunchTo)}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="text-end pe-4">
                                    <div className="btn-group">
                                        {editingId === day.id ? (
                                            <>
                                                <button
                                                    className="btn btn-success"
                                                    onClick={handleSubmit((data) => updateDoctorHours(day.id, data))}
                                                >
                                                    <i className="bi bi-check"></i>
                                                </button>
                                                <button className="btn btn-outline-secondary" onClick={handleCancel}>
                                                    <i className="bi bi-x"></i>
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    className="btn btn-outline-info"
                                                    onClick={() => handleEdit(day)}
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </button>

                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
                </div>
            </div>
        </>
    );
};

export default DoctorHours;